import { LeadOwnerEmails } from "@/entities/hubspot/types";
import {
  WebFormFieldMapUpdate,
  WebFormRoutingUpdate
} from "@/entities/webForm/types";

// The backend's limits on a routing or field map save.
export const MAX_OWNER_RULES = 100;
export const MAX_FIELD_MAPPINGS = 100;
export const MAX_NAME_LENGTH = 255;

// One origin per line; blank lines are ignored.
export const parseOrigins = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

// Intake matches form fields and routing values ignoring case and extra spaces.
const fold = (value: string) =>
  value.split(/\s+/).filter(Boolean).join(" ").toLowerCase();

const hasDuplicate = (names: string[]) => {
  const folded = names.map(fold);
  return new Set(folded).size !== folded.length;
};

const hasOwner = (owners: LeadOwnerEmails) =>
  Boolean(owners.sdrOwner || owners.aeOwner);

export type RoutingError =
  | "fieldRequired"
  | "valueRequired"
  | "ownerRequired"
  | "duplicateValue";

// Inputs keep what was typed so spaces between words survive; padding is
// trimmed here. Fallback owners with no role set mean no fallback.
export const routingUpdate = (
  routingField: string,
  rules: WebFormRoutingUpdate["ownerRules"],
  fallback: LeadOwnerEmails
): WebFormRoutingUpdate => ({
  ownerRoutingField: routingField.trim(),
  ownerRules: rules.map((rule) => ({ ...rule, value: rule.value.trim() })),
  fallbackOwners: hasOwner(fallback) ? fallback : null
});

// The reasons the backend would refuse this routing, checked before saving.
export const routingErrors = (update: WebFormRoutingUpdate): RoutingError[] => {
  const errors: RoutingError[] = [];
  if (!update.ownerRoutingField) errors.push("fieldRequired");
  if (update.ownerRules.some((rule) => !rule.value))
    errors.push("valueRequired");
  if (update.ownerRules.some((rule) => !hasOwner(rule)))
    errors.push("ownerRequired");
  if (hasDuplicate(update.ownerRules.map((rule) => rule.value).filter(Boolean)))
    errors.push("duplicateValue");
  return errors;
};

export type FieldMapError =
  | "formFieldRequired"
  | "leadFieldRequired"
  | "duplicateFormField";

export const fieldMapUpdate = (
  mappings: WebFormFieldMapUpdate["leadFieldMap"]
): WebFormFieldMapUpdate => ({
  leadFieldMap: mappings.map((mapping) => ({
    ...mapping,
    formField: mapping.formField.trim()
  }))
});

// The reasons the backend would refuse this mapping, checked before saving.
export const fieldMapErrors = (
  update: WebFormFieldMapUpdate
): FieldMapError[] => {
  const mappings = update.leadFieldMap;
  const errors: FieldMapError[] = [];
  if (mappings.some((mapping) => !mapping.formField))
    errors.push("formFieldRequired");
  if (mappings.some((mapping) => !mapping.leadField))
    errors.push("leadFieldRequired");
  if (
    hasDuplicate(mappings.map((mapping) => mapping.formField).filter(Boolean))
  )
    errors.push("duplicateFormField");
  return errors;
};
